import React, { useState, useRef } from 'react';
import { UploadIcon, TrashIcon, CheckIcon, XIcon } from 'lucide-react';
export interface LogoAsset {
  id: string;
  scope: 'Tenant' | 'Entity' | 'Site';
  entityId?: string;
  entityName?: string;
  siteId?: string;
  siteName?: string;
  fileName: string;
  url: string;
  contentType: string;
  uploadedAt: string;
  uploadedByUserId: string;
  uploadedByUserName: string;
  isActive: boolean;
}
interface LogoManagementProps {
  scope: 'Tenant' | 'Entity' | 'Site';
  entityId?: string;
  entityName?: string;
  siteId?: string;
  siteName?: string;
  logos: LogoAsset[];
  onUpload: (file: File, scope: 'Tenant' | 'Entity' | 'Site', entityId?: string, siteId?: string) => void;
  onDelete: (logoId: string) => void;
  onSetActive: (logoId: string) => void;
  locale: string;
}
export function LogoManagement({
  scope,
  entityId,
  entityName,
  siteId,
  siteName,
  logos,
  onUpload,
  onDelete,
  onSetActive,
  locale
}: LogoManagementProps) {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoTypes = ['Primary Logo', 'Secondary Logo', 'Icon', 'Letterhead', 'Other'];
  const [selectedLogoType, setSelectedLogoType] = useState(logoTypes[0]);
  // Filter logos based on scope and entity/site
  const filteredLogos = logos.filter(logo => {
    if (scope === 'Tenant') {
      return logo.scope === 'Tenant';
    } else if (scope === 'Entity') {
      return logo.scope === 'Entity' && logo.entityId === entityId || logo.scope === 'Tenant';
    } else if (scope === 'Site') {
      return logo.scope === 'Site' && logo.siteId === siteId || logo.scope === 'Entity' && logo.entityId === entityId || logo.scope === 'Tenant';
    }
    return false;
  });
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      onUpload(file, scope, entityId, siteId);
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      // Add logo type as document type
      const fileWithType = {
        file: file,
        documentType: selectedLogoType,
        name: file.name,
        size: file.size,
        uploadDate: new Date().toISOString()
      };
      onUpload(file, scope, entityId, siteId);
    }
  };
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };
  const handleLogoTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLogoType(e.target.value);
  };
  return <div className="space-y-6" data-id="element-16132">
      <div data-id="element-16133">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-16134">Logo Management</h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-16135">
          Upload and manage logos for{' '}
          {scope === 'Tenant' ? 'the entire system' : scope === 'Entity' ? entityName : siteName}
        </p>
      </div>
      <div className="space-y-3" data-id="element-16136">
        <div data-id="element-16137">
          <label htmlFor="logoType" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-16138">
            Logo Type
          </label>
          <select id="logoType" value={selectedLogoType} onChange={handleLogoTypeChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-16139">
            {logoTypes.map(type => <option key={type} value={type} data-id="element-16140">
                {type}
              </option>)}
          </select>
        </div>
        <div className={`border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center ${dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300'}`} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} data-id="element-16141">
          <div className="text-center" data-id="element-16142">
            <UploadIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-16143" />
            <div className="mt-2" data-id="element-16144">
              <p className="text-sm text-gray-600" data-id="element-16145">
                Drag and drop logo here, or
                <button type="button" className="ml-1 text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline" onClick={handleButtonClick} data-id="element-16146">
                  browse
                </button>
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-16147">
                Upload PNG, JPG, or SVG (max 2MB)
              </p>
            </div>
          </div>
          <input ref={fileInputRef} type="file" onChange={handleFileChange} className="hidden" accept="image/png,image/jpeg,image/svg+xml" data-id="element-16148" />
        </div>
      </div>
      {filteredLogos.length > 0 && <div data-id="element-16149">
          <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-16150">
            Uploaded Logos
          </h4>
          <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-16151">
            <ul className="divide-y divide-gray-200" data-id="element-16152">
              {filteredLogos.map(logo => <li key={logo.id} className="px-4 py-3 flex items-center justify-between" data-id="element-16153">
                  <div className="flex items-center" data-id="element-16154">
                    <div className="flex-shrink-0 h-12 w-12 bg-gray-100 rounded-md overflow-hidden" data-id="element-16155">
                      <img src={logo.url} alt={`Logo for ${logo.scope === 'Tenant' ? 'Tenant' : logo.scope === 'Entity' ? logo.entityName : logo.siteName}`} className="h-12 w-12 object-contain" data-id="element-16156" />
                    </div>
                    <div className="ml-4" data-id="element-16157">
                      <p className="text-sm font-medium text-gray-900" data-id="element-16158">
                        {logo.fileName}
                      </p>
                      <div className="mt-1 flex items-center" data-id="element-16159">
                        <span className="text-xs text-gray-500" data-id="element-16160">
                          {new Date(logo.uploadedAt).toLocaleDateString(locale)}
                        </span>
                        <span className="ml-2 text-xs text-gray-500" data-id="element-16161">
                          {logo.scope}
                        </span>
                        <span className="ml-2 text-xs" data-id="element-16162">
                          {logo.isActive ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-16163">
                              Active
                            </span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-16164">
                              Inactive
                            </span>}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2" data-id="element-16165">
                    {!logo.isActive && <button type="button" onClick={() => onSetActive(logo.id)} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" title="Set as active" data-id="element-16166">
                        <CheckIcon className="h-4 w-4" data-id="element-16167" />
                      </button>}
                    <button type="button" onClick={() => onDelete(logo.id)} className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" title="Delete logo" data-id="element-16168">
                      <TrashIcon className="h-4 w-4" data-id="element-16169" />
                    </button>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>}
    </div>;
}